import { useContext, useEffect } from "react";

import { StyledEngineProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { SnackbarHost, toast } from "@rubi/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import { CookiesProvider } from "react-cookie";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "@rubi/ui/tokens.css";
import { setLicenseKey, slk, settings as coreSettings } from "survey-core";
import "survey-core/survey.i18n";
import { settings } from "survey-creator-core";
import "survey-creator-core/survey-creator-core.i18n";
import "./i18n";

import "./App.css";

import ReportV2TrainingRedirect from "@/components/online-reporting/ReportV2TrainingRedirect";

import { DrawerProvider } from "./common/SlideoutDrawer/DrawerContext";
import "./common/common.css";
import { SURVEYJS_SUPPORTED_LOCALES } from "./common/constants/languages";
import { devEnvironment } from "./common/featureFlags";
import { isInternalPath } from "./common/internalPath";
import { configureSurveyChoicesAuth } from "./common/surveyChoicesAuth";
import AddPhone from "./components/addphone/AddPhone";
import { UpdateAlert } from "./components/alerts/add/UpdateAlert";
import { BulletinFormPage } from "./components/alerts/add-v2/BulletinFormPage";
import {
  BULLETIN_EDIT_ROUTE,
  BULLETIN_FORM_ROUTE,
  BULLETIN_START_ROUTE,
} from "./components/alerts/add-v2/routePaths";
import { StartScreen } from "./components/alerts/add-v2/start/StartScreen";
import { AlertSingle } from "./components/alerts/AlertSingle";
import { AlertList } from "./components/alerts/AlertsList";
import AlertTraining from "./components/alerts/AlertTraining";
import { AlertView } from "./components/alerts/AlertView";
import AlertAnalytics from "./components/alerts/analytics/AlertAnalytics";
import { BulletinShare } from "./components/alerts/BulletinShare/BulletinShare";
import BulletinShareList from "./components/alerts/BulletinShare/BulletinShareList";
import { ALERT_STATUS } from "./components/alerts/constants";
import {
  AlertViewProvider,
  SingleAlertProvider,
} from "./components/alerts/context";
import { AlertGroupManagementDashboard } from "./components/alerts/GroupManagement";
import AlertGroupBulletinSharePending from "./components/alerts/GroupManagement/AlertGroupBulletinSharePending";
import AlertGroupCreate from "./components/alerts/GroupManagement/AlertGroupCreate";
import AlertGroupManagement from "./components/alerts/GroupManagement/AlertGroupManagement";
import AlertGroupUpdate from "./components/alerts/GroupManagement/AlertGroupUpdate";
import AlertGroupUserManagement from "./components/alerts/GroupManagement/AlertGroupUserManagement";
import AlertGroupUsersList from "./components/alerts/GroupManagement/AlertGroupUsersList";
import { PublicAlertDetail } from "./components/alerts/public/PublicAlertDetail";
import { PublicAlertList } from "./components/alerts/public/PublicAlertList";
import AlertBulletinTypeSettings from "./components/alerts/settings/AlertBulletinTypeSettings";
import AlertCrimeTypeSettings from "./components/alerts/settings/AlertCrimeTypeSettings";
import {
  AddRegistryPage,
  MapRegistryPage,
  SettingsPage,
} from "./components/canvas-registry";
import { ChangePassword } from "./components/changepassword/ChangePassword";
import SetPassword from "./components/changepassword/SetPassword";
import { AppSelector } from "./components/dashboard";
import {
  DevHomePage,
  OnboardCommunityWizard,
  DevOrgConfigListPage,
  DevOrgConfigPage,
  DevOrgPage,
  DevOrgsPage,
  DevPartnerOrgPage,
  DevPartnerOrgsPage,
  DevPermissionPage,
  DevPermissionsPage,
  DevRmsExportErrorDetailPage,
  DevRmsExportErrorsPage,
  DevScheduledJobsPage,
  DevTranslateAllNameDescriptionValues,
  LinterSchemaPage,
  OnboardPartnerWizard,
  DevRegionOrgListPage,
  DevGeoDataFileListPage,
  DevGeoDataFilePage,
  DevGeoDataFileProcessingRunPage,
  DevSubregionListPage,
  DevSubregionPage,
  DevRegionListPage,
  DevRegionCreatePage,
  DevRegionPage,
} from "./components/dev";
import FeedbackApp from "./components/feedback/FeedbackApp";
import IntelDashboard from "./components/intel/IntelDashboard";
import MatchReportsList from "./components/intel/MatchReportsList";
import RequestSubmissionPage from "./components/intel/RequestSubmissionPage";
import AdvancedNotificationsPage from "./components/online-reporting/AdvancedNotificationsPage";
import CISODraftsList from "./components/online-reporting/CISODraftsList";
import CISOReportSelect from "./components/online-reporting/CISOReportSelect";
import CISOSubmissionComplete from "./components/online-reporting/CISOSubmissionComplete";
import CISOSubmissionCreate from "./components/online-reporting/CISOSubmissionCreate";
import CitizenEngage from "./components/online-reporting/citizen-engage/CitizenEngage";
import OnlineReportingProvider from "./components/online-reporting/context";
import { BulkEditPreview } from "./components/online-reporting/form-management/BulkEditPreview";
import CrimeCodeManagement from "./components/online-reporting/form-management/CrimeCodeManagement";
import DropdownConfiguration from "./components/online-reporting/form-management/DropdownConfiguration";
import { FormContextProvider } from "./components/online-reporting/form-management/FormContextProvider";
import FormListView from "./components/online-reporting/form-management/FormListView";
import ItemTypeOptionView from "./components/online-reporting/form-management/ItemTypeOptionView";
import FormManagementOverview from "./components/online-reporting/form-management/Overview";
import { SurveyConfigurationEdit } from "./components/online-reporting/form-management/SurveyConfigurationEdit";
import { SurveyEdit } from "./components/online-reporting/form-management/SurveyEdit";
import SurveyGroupManagement from "./components/online-reporting/form-management/SurveyGroupManagement";
import SurveyGroupMembersPage from "./components/online-reporting/form-management/SurveyGroupMembersPage";
import SurveyListView from "./components/online-reporting/form-management/SurveyListView";
import { CondensedReportProvider } from "./components/online-reporting/report-view/CondensedReportProvider";
import CondensedReportView from "./components/online-reporting/report-view/CondensedReportView";
import ImageMatchReport from "./components/online-reporting/report-view/ImageMatchReport";
import ReportV2Analytics from "./components/online-reporting/ReportV2Analytics";
import ReportV2List from "./components/online-reporting/ReportV2List";
import ReportV2Settings from "./components/online-reporting/ReportV2Settings";
import ReportV2Training from "./components/online-reporting/ReportV2Training";
import ViewReport from "./components/online-reporting/ViewReport";
import PartnerReportDetail from "./components/partner/PartnerReportDetail";
import PartnerReportList from "./components/partner/PartnerReportList";
import {
  RegistryDashboard,
  RegistryEntries,
  RegistryEntry,
  RegistrySettings,
} from "./components/registry-system";
import { RegistryProvider } from "./components/registry-system/context/RegistryContext";
import RegistryCreate from "./components/registry-system/RegistryCreate";
import UserPage from "./components/UserManagement/User/UserPage/UserPage";
import UsersList from "./components/UserManagement/User/UsersList";
import VerifyPhone from "./components/verifyphone/VerifyPhone";
import { AuthStateContext, AuthStateProvider } from "./contexts/AuthContext";
import { AutoRefreshProvider } from "./contexts/AutoRefreshContext";
import { ColorModeProvider } from "./contexts/ColorModeContext";
import { DevEnvironmentProvider } from "./contexts/DevContext";
import ImageMatchProvider from "./contexts/ImageMatchContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { UserProvider } from "./contexts/UserContext";
import { APP_ALERT_SERVER_URL, APP_SURVEYJS_KEY } from "./environment";
import { CommunitiesPreloader } from "./hooks/useCommunities";
import { NavigationGuardProvider } from "./hooks/useNavigationGuard";
import { Home } from "./layout";
import MainLayout from "./layout/MainLayout";
import { ServiceLogin } from "./layout/ServiceLogin";
import ProtectedPage from "./ProtectedPage";
import { rubiConnectRoutes } from "./routes/RubiConnectRoutes";
import { rubiMessageRoutes } from "./routes/RubiMessageRoutes";
import "./styles/_utils.scss";

export const APIUrl = APP_ALERT_SERVER_URL;
const queryClient = new QueryClient();

const MFARequired = () => {
  useEffect(() => {
    toast.error(
      "Multi-factor authentication must be configured to use this login flow. Please reset your password and configure multi-factor authentication.",
    );
  }, []);
  return <Home />;
};

const HashCatch = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.hash.startsWith("#/")) return;

    const target = location.hash.slice(1);
    if (isInternalPath(target)) {
      navigate(target); // or history.replace
    }
  }, []);

  return <></>;
};

const ProfileUpdateCatch = () => {
  const { currentUser } = useContext(AuthStateContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.profile_update_required) {
      toast.info("Please update your profile information before proceeding.");
      navigate(`/users/${currentUser.id}/profile/edit`); // or history.replace
    } else {
      console.log("No profile update required");
    }
  }, [currentUser]);

  return <></>;
};

const Rv2RedirectToOrgURL = ({ appPath }: { appPath: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const { currentUser } = useContext(AuthStateContext);

  useEffect(() => {
    const newPath = pathname.replace(
      `/${appPath}`,
      `/${appPath}/${currentUser?.rubi_org_id}`,
    );
    navigate(newPath);
  }, []);
  return <></>;
};

const PartnerReportsEntry = () => {
  const { currentUser } = useContext(AuthStateContext);
  const partnerOrgId = currentUser?.partner_org_id;
  if (partnerOrgId) {
    return <Navigate to={`/rubipartner/${partnerOrgId}/reports`} replace />;
  }
  return <PartnerReportList />;
};

const StoreUtcDates = () => {
  useEffect(() => {
    coreSettings.storeUtcDates = true;
  }, []);

  return <></>;
};

// The whole app tree renders inside a single splat route of a data router (see
// `router` below). The descendant <Routes> here keeps working exactly as it did
// under <BrowserRouter>, but navigation now goes through the data router, which
// is what makes useBlocker() (see useNavigationGuard) available app-wide.
const AppShell = () => {
  const { PUBLISHED, DRAFT } = ALERT_STATUS;

  return (
    <QueryClientProvider client={queryClient}>
      <CommunitiesPreloader />
      <NavigationGuardProvider>
        <SidebarProvider>
          <FormContextProvider>
            <CookiesProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DevEnvironmentProvider>
                  <UserProvider>
                    <AuthStateProvider>
                      <RegistryProvider>
                        <AlertViewProvider>
                          <OnlineReportingProvider>
                            <ImageMatchProvider>
                              <HashCatch />
                              <ProfileUpdateCatch />
                              <StoreUtcDates />
                              <Routes>
                                <Route path="/login" element={<Home />} />
                                <Route
                                  path="/rubialert/public/group/:groupId"
                                  element={<PublicAlertList />}
                                />
                                <Route
                                  path="/rubialert/public/group/:groupId/bulletin"
                                  element={<PublicAlertDetail />}
                                />
                                {/* TODO pre-pend with rubialert */}
                                <Route
                                  path={"/view-alert"}
                                  element={
                                    <SingleAlertProvider>
                                      <AlertView />
                                    </SingleAlertProvider>
                                  }
                                />
                                <Route
                                  path="/setpassword"
                                  element={<SetPassword />}
                                />
                                <Route
                                  path="/changepassword"
                                  element={<ChangePassword />}
                                />
                                <Route
                                  path="/addphone"
                                  element={<AddPhone />}
                                />
                                <Route
                                  path="/verifyphone"
                                  element={<VerifyPhone verifyType="phone" />}
                                />
                                <Route
                                  path="/confirmotp"
                                  element={
                                    <VerifyPhone
                                      verifyType="phone"
                                      previouslyConfirmed
                                    />
                                  }
                                />
                                <Route
                                  path="/verifyemail"
                                  element={<VerifyPhone verifyType="email" />}
                                />
                                <Route
                                  path="/resetpassword"
                                  element={
                                    <ChangePassword
                                      oldPasswordRequired={false}
                                    />
                                  }
                                />
                                <Route
                                  path="/mfarequired"
                                  element={<MFARequired />}
                                />
                                <Route
                                  path="/:organizationSlug/login"
                                  element={<ServiceLogin />}
                                />
                                {/* Developer and Administrator Utilities. Gated as a group so a
                                    route added here cannot be reachable while signed out. */}
                                <Route
                                  element={
                                    <ProtectedPage>
                                      <Outlet />
                                    </ProtectedPage>
                                  }
                                >
                                  <Route
                                    path="/dev_admin"
                                    element={<DevHomePage />}
                                  />
                                  <Route
                                    path="/dev_admin/scheduled_job"
                                    element={<DevScheduledJobsPage />}
                                  />
                                  <Route
                                    path="/dev_admin/permission"
                                    element={<DevPermissionsPage />}
                                  />
                                  <Route
                                    path="/dev_admin/permission/:id"
                                    element={<DevPermissionPage />}
                                  />
                                  <Route
                                    path="/dev_admin/org"
                                    element={<DevOrgsPage />}
                                  />
                                  <Route
                                    path="/dev_admin/org/:id"
                                    element={<DevOrgPage />}
                                  />
                                  <Route
                                    path="/dev_admin/onboard"
                                    element={<OnboardCommunityWizard />}
                                  />
                                  <Route
                                    path="/dev_admin/partner_org"
                                    element={<DevPartnerOrgsPage />}
                                  />
                                  <Route
                                    path="/dev_admin/partner_org/:id"
                                    element={<DevPartnerOrgPage />}
                                  />
                                  <Route
                                    path="/dev_admin/onboard_partner"
                                    element={<OnboardPartnerWizard />}
                                  />
                                  <Route
                                    path="/dev_admin/org_config"
                                    element={<DevOrgConfigListPage />}
                                  />
                                  <Route
                                    path="/dev_admin/org_config/:id"
                                    element={<DevOrgConfigPage />}
                                  />
                                  <Route
                                    path="/dev_admin/rms_export_error"
                                    element={<DevRmsExportErrorsPage />}
                                  />
                                  <Route
                                    path="/dev_admin/rms_export_error/:id"
                                    element={<DevRmsExportErrorDetailPage />}
                                  />
                                  <Route
                                    path="/dev_admin/translate-all-surveys"
                                    element={
                                      <DevTranslateAllNameDescriptionValues />
                                    }
                                  />
                                  <Route
                                    path="/dev_admin/linter-schema"
                                    element={<LinterSchemaPage />}
                                  />
                                  <Route
                                    path="/dev_admin/crime-types"
                                    element={<AlertCrimeTypeSettings />}
                                  />
                                  <Route
                                    path="/dev_admin/bulletin-types"
                                    element={<AlertBulletinTypeSettings />}
                                  />
                                  <Route
                                    path="/dev_admin/region"
                                    element={<DevRegionOrgListPage />}
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/geo_data_file"
                                    element={<DevGeoDataFileListPage />}
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/geo_data_file/:fileId"
                                    element={<DevGeoDataFilePage />}
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/geo_data_file/:fileId/run/:runId"
                                    element={
                                      <DevGeoDataFileProcessingRunPage />
                                    }
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/subregion"
                                    element={<DevSubregionListPage />}
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/subregion/:subregionId"
                                    element={<DevSubregionPage />}
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/region"
                                    element={<DevRegionListPage />}
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/region/new"
                                    element={<DevRegionCreatePage />}
                                  />
                                  <Route
                                    path="/dev_admin/region/:orgId/region/:regionId"
                                    element={<DevRegionPage />}
                                  />
                                </Route>
                                <Route
                                  path="/"
                                  element={
                                    <AutoRefreshProvider>
                                      <MainLayout />
                                    </AutoRefreshProvider>
                                  }
                                >
                                  <Route
                                    path="/"
                                    element={
                                      <ProtectedPage>
                                        <AppSelector />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* RubiReportV2 */}
                                  <Route
                                    path="/rubireportv2/:orgId/reports"
                                    element={
                                      <ProtectedPage>
                                        <ReportV2List cisoDisplay={false} />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/report/:id"
                                    element={
                                      <ProtectedPage>
                                        <ViewReport editing={false} />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/report/:id/edit"
                                    element={
                                      <ProtectedPage>
                                        <ViewReport editing={true} />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/report/:id/condensed"
                                    element={
                                      <ProtectedPage>
                                        <CondensedReportProvider>
                                          <CondensedReportView
                                            cisoDisplay={false}
                                          />
                                        </CondensedReportProvider>
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/analytics"
                                    element={
                                      <ProtectedPage>
                                        <CondensedReportProvider>
                                          <ReportV2Analytics
                                            cisoDisplay={false}
                                          />
                                        </CondensedReportProvider>
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/training"
                                    element={
                                      <ProtectedPage>
                                        <ReportV2TrainingRedirect />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/training"
                                    element={
                                      <ProtectedPage>
                                        <ReportV2Training />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/settings"
                                    element={
                                      <ProtectedPage>
                                        <ReportV2Settings />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/settings/advanced-notifications"
                                    element={
                                      <ProtectedPage>
                                        <AdvancedNotificationsPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* Form Management */}
                                  <Route
                                    path="/rubireportv2/:orgId/forms"
                                    element={
                                      <ProtectedPage>
                                        <FormManagementOverview />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/configs"
                                    element={
                                      <ProtectedPage>
                                        <FormListView />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/configs/:surveyClass"
                                    element={
                                      <ProtectedPage>
                                        <SurveyConfigurationEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/configs/new"
                                    element={
                                      <ProtectedPage>
                                        <SurveyConfigurationEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/configs/:configId/edit"
                                    element={
                                      <ProtectedPage>
                                        <SurveyConfigurationEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/dropdowns"
                                    element={
                                      <ProtectedPage>
                                        <DropdownConfiguration />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/dropdowns/new"
                                    element={
                                      <ProtectedPage>
                                        <ItemTypeOptionView />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/dropdowns/:listId"
                                    element={
                                      <ProtectedPage>
                                        <ItemTypeOptionView />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/dropdowns/:listId/edit"
                                    element={
                                      <ProtectedPage>
                                        <ItemTypeOptionView />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/crime-codes"
                                    element={
                                      <ProtectedPage>
                                        <CrimeCodeManagement />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/groups"
                                    element={
                                      <ProtectedPage>
                                        <SurveyGroupManagement />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/groups/:groupId"
                                    element={
                                      <ProtectedPage>
                                        <SurveyGroupMembersPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/surveys"
                                    element={
                                      <ProtectedPage>
                                        <SurveyListView />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/surveys/bulk-edit/preview"
                                    element={
                                      <ProtectedPage>
                                        <BulkEditPreview />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/surveys/:surveyId"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/surveys/new"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/surveys/:surveyId/edit"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/forms/surveys/:surveyId/edit/existing"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/*"
                                    element={
                                      <ProtectedPage>
                                        <Rv2RedirectToOrgURL appPath="rubireportv2" />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubireportv2/:orgId/citizen-engage"
                                    element={
                                      <ProtectedPage>
                                        <CitizenEngage />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* RubiFEEDBACK — dev environment only */}
                                  {devEnvironment() && (
                                    <>
                                      <Route
                                        path="/rubifeedback/:orgId/*"
                                        element={
                                          <ProtectedPage>
                                            <DrawerProvider>
                                              <FeedbackApp />
                                            </DrawerProvider>
                                          </ProtectedPage>
                                        }
                                      />
                                      <Route
                                        path="/rubifeedback/*"
                                        element={
                                          <ProtectedPage>
                                            <Rv2RedirectToOrgURL appPath="rubifeedback" />
                                          </ProtectedPage>
                                        }
                                      />
                                    </>
                                  )}
                                  {/* RubiPARTNER */}
                                  <Route
                                    path="/rubipartner/reports"
                                    element={
                                      <ProtectedPage>
                                        <PartnerReportsEntry />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubipartner/:orgId/reports"
                                    element={
                                      <ProtectedPage>
                                        <PartnerReportList />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubipartner/:orgId/report/:id/condensed"
                                    element={
                                      <ProtectedPage>
                                        <PartnerReportDetail />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubipartner/*"
                                    element={
                                      <ProtectedPage>
                                        <Navigate
                                          to="/rubipartner/reports"
                                          replace
                                        />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* RubiINTEL */}
                                  <Route
                                    path="/rubiintel/:orgId/reports"
                                    element={
                                      <ProtectedPage>
                                        <DrawerProvider>
                                          <ReportV2List cisoDisplay={true} />
                                        </DrawerProvider>
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/ocis"
                                    element={
                                      <ProtectedPage>
                                        <CISOReportSelect />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/drafts"
                                    element={
                                      <ProtectedPage>
                                        <CISODraftsList />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/request-submission"
                                    element={
                                      <ProtectedPage>
                                        <RequestSubmissionPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/report/new/:surveyClass"
                                    element={
                                      <ProtectedPage>
                                        <CISOSubmissionCreate />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/report/:submissionId/complete"
                                    element={
                                      <ProtectedPage>
                                        <CISOSubmissionComplete />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/report/:id"
                                    element={
                                      <ProtectedPage>
                                        <ViewReport editing={false} />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/report/:id/edit"
                                    element={
                                      <ProtectedPage>
                                        <ViewReport editing={true} />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/report/:id/condensed"
                                    element={
                                      <ProtectedPage>
                                        <CondensedReportProvider>
                                          <CondensedReportView
                                            cisoDisplay={true}
                                          />
                                        </CondensedReportProvider>
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/match-report/:matchReportId/:attachmentId"
                                    element={
                                      <ProtectedPage>
                                        <ImageMatchReport />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/match-reports"
                                    element={
                                      <ProtectedPage>
                                        <MatchReportsList />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/dashboard"
                                    element={
                                      <ProtectedPage>
                                        <IntelDashboard />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/settings"
                                    element={
                                      <ProtectedPage>
                                        <ReportV2Settings />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/settings/advanced-notifications"
                                    element={
                                      <ProtectedPage>
                                        <AdvancedNotificationsPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms"
                                    element={
                                      <ProtectedPage>
                                        <FormManagementOverview />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/configs"
                                    element={
                                      <ProtectedPage>
                                        <FormListView />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/configs/:surveyClass"
                                    element={
                                      <ProtectedPage>
                                        <SurveyConfigurationEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/configs/new"
                                    element={
                                      <ProtectedPage>
                                        <SurveyConfigurationEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/configs/:configId/edit"
                                    element={
                                      <ProtectedPage>
                                        <SurveyConfigurationEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/crime-codes"
                                    element={
                                      <ProtectedPage>
                                        <CrimeCodeManagement />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/groups"
                                    element={
                                      <ProtectedPage>
                                        <SurveyGroupManagement />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/groups/:groupId"
                                    element={
                                      <ProtectedPage>
                                        <SurveyGroupMembersPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/surveys"
                                    element={
                                      <ProtectedPage>
                                        <SurveyListView />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/surveys/:surveyId"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/surveys/new"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/surveys/:surveyId/edit"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/:orgId/forms/surveys/:surveyId/edit/existing"
                                    element={
                                      <ProtectedPage>
                                        <SurveyEdit />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiintel/*"
                                    element={
                                      <ProtectedPage>
                                        <Rv2RedirectToOrgURL appPath="rubiintel" />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* RubiALERT */}
                                  <Route
                                    path="/rubialert/alerts"
                                    element={
                                      <ProtectedPage>
                                        {/* This is needed for drawer view */}
                                        {/* <SingleAlertProvider> */}
                                        <DrawerProvider>
                                          <AlertList />
                                        </DrawerProvider>
                                        {/* </SingleAlertProvider> */}
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/alerts/:id/single"
                                    element={
                                      <SingleAlertProvider>
                                        <ProtectedPage>
                                          <AlertSingle mode={PUBLISHED} />
                                        </ProtectedPage>
                                      </SingleAlertProvider>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/alerts/:id/single/edit"
                                    element={
                                      <SingleAlertProvider>
                                        <ProtectedPage>
                                          <UpdateAlert mode="edit" />
                                        </ProtectedPage>
                                      </SingleAlertProvider>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/drafts"
                                    element={
                                      <ProtectedPage>
                                        <DrawerProvider>
                                          <AlertList isDraftsList />
                                        </DrawerProvider>
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/drafts/:id/single"
                                    element={
                                      <SingleAlertProvider>
                                        <ProtectedPage>
                                          <AlertSingle mode={DRAFT} />
                                        </ProtectedPage>
                                      </SingleAlertProvider>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/drafts/:id/single/edit"
                                    element={
                                      <SingleAlertProvider>
                                        <ProtectedPage>
                                          <UpdateAlert mode="edit" />
                                        </ProtectedPage>
                                      </SingleAlertProvider>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/add-alert"
                                    element={
                                      <SingleAlertProvider>
                                        <ProtectedPage>
                                          <UpdateAlert mode="add" />
                                        </ProtectedPage>
                                      </SingleAlertProvider>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/upload-alert"
                                    element={
                                      <SingleAlertProvider>
                                        <ProtectedPage>
                                          <UpdateAlert mode="upload" />
                                        </ProtectedPage>
                                      </SingleAlertProvider>
                                    }
                                  />
                                  {/* add-v2 — the bulletin creation flow. The `/rubialert/add-alert`
                                      and `/rubialert/upload-alert` routes above are no longer linked
                                      from anywhere, but stay reachable by URL as a fallback. */}
                                  <Route
                                    path={BULLETIN_START_ROUTE}
                                    element={
                                      <ProtectedPage>
                                        <StartScreen />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path={BULLETIN_FORM_ROUTE}
                                    element={
                                      <ProtectedPage>
                                        <BulletinFormPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* Same component: the route is the mode, and
                                      `:bulletinId` is the whole of the difference. */}
                                  <Route
                                    path={BULLETIN_EDIT_ROUTE}
                                    element={
                                      <ProtectedPage>
                                        <BulletinFormPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/analytics"
                                    element={
                                      <ProtectedPage>
                                        <AlertAnalytics />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/training"
                                    element={
                                      <ProtectedPage>
                                        <AlertTraining />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/manage-groups"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupManagement />
                                      </ProtectedPage>
                                    }
                                  />

                                  <Route
                                    path="/rubialert/manage-groups/:groupId/members"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupUsersList />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* Bulletin Share */}
                                  <Route
                                    path="/rubialert/bulletin-share"
                                    element={
                                      <ProtectedPage>
                                        <BulletinShareList />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/bulletin-share/:shareId"
                                    element={
                                      <ProtectedPage>
                                        <BulletinShare />
                                      </ProtectedPage>
                                    }
                                  />

                                  <Route
                                    path="/rubialert/manage-groups/:groupId/"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupManagementDashboard />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/manage-groups/create"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupCreate />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/manage-groups/:groupId/edit"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupUpdate />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/manage-groups/:groupId/members"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupUsersList />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/manage-groups/:groupId/bulletin-share/pending"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupBulletinSharePending />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubialert/manage-groups/:groupId/members/:userId/permissions"
                                    element={
                                      <ProtectedPage>
                                        <AlertGroupUserManagement />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* RubiCANVAS */}
                                  <Route
                                    path="/rubicanvas/map"
                                    element={
                                      <ProtectedPage>
                                        <MapRegistryPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubicanvas/add-camera"
                                    element={
                                      <ProtectedPage>
                                        <AddRegistryPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubicanvas/settings"
                                    element={
                                      <ProtectedPage>
                                        <SettingsPage />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* RubiREGISTRY */}
                                  <Route
                                    path="/rubiregistry/:orgId/registries"
                                    element={
                                      <ProtectedPage>
                                        <RegistryEntries />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiregistry/:orgId/:registryName/:entryId"
                                    element={
                                      <ProtectedPage>
                                        <RegistryEntry />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiregistry/:orgId/:registryName/:entryId/edit"
                                    element={
                                      <ProtectedPage>
                                        <RegistryCreate />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiregistry/:orgId/:registryName/new"
                                    element={
                                      <ProtectedPage>
                                        <RegistryCreate />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiregistry/:orgId/dashboard"
                                    element={
                                      <ProtectedPage>
                                        <RegistryDashboard />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiregistry/:orgId/settings"
                                    element={
                                      <ProtectedPage>
                                        <RegistrySettings />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/rubiregistry/*"
                                    element={
                                      <ProtectedPage>
                                        <Rv2RedirectToOrgURL appPath="rubiregistry" />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* Users List */}
                                  <Route
                                    path={"organizations/:orgId/users"}
                                    element={
                                      <ProtectedPage>
                                        <UsersList />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* View User */}
                                  <Route
                                    path={"organizations/:orgId/users/:userId"}
                                    element={
                                      <ProtectedPage>
                                        <UserPage mode="view" profile={false} />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* Add User */}
                                  <Route
                                    path={"organizations/:orgId/users/new"}
                                    element={
                                      <ProtectedPage>
                                        <UserPage mode="add" profile={false} />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* Update User */}
                                  <Route
                                    path={
                                      "organizations/:orgId/users/:userId/edit"
                                    }
                                    element={
                                      <ProtectedPage>
                                        <UserPage mode="edit" profile={false} />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path={"users/:userId/profile"}
                                    element={
                                      <ProtectedPage>
                                        <UserPage mode="view" profile />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path={"users/:userId/profile/edit"}
                                    element={
                                      <ProtectedPage>
                                        <UserPage mode="edit" profile />
                                      </ProtectedPage>
                                    }
                                  />
                                  <Route
                                    path="/organizations/*"
                                    element={
                                      <ProtectedPage>
                                        <Rv2RedirectToOrgURL appPath="organizations" />
                                      </ProtectedPage>
                                    }
                                  />
                                  {/* RubiMESSAGE */}
                                  {rubiMessageRoutes}
                                  {rubiConnectRoutes}
                                </Route>
                              </Routes>
                            </ImageMatchProvider>
                          </OnlineReportingProvider>
                        </AlertViewProvider>
                      </RegistryProvider>
                    </AuthStateProvider>
                  </UserProvider>
                </DevEnvironmentProvider>
                <SnackbarHost />
              </LocalizationProvider>
            </CookiesProvider>
          </FormContextProvider>
        </SidebarProvider>
      </NavigationGuardProvider>
    </QueryClientProvider>
  );
};

const router = createBrowserRouter([{ path: "*", element: <AppShell /> }]);

const App = () => {
  setLicenseKey(APP_SURVEYJS_KEY);
  settings.translation.maximumSelectedLocales =
    SURVEYJS_SUPPORTED_LOCALES.length;

  useEffect(() => {
    slk(APP_SURVEYJS_KEY);
    configureSurveyChoicesAuth();

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Ignore login and user auth status checks
        // This was disrupting the auth context from capturing the redirection flow
        if (
          window.location.href &&
          !window.location.href.includes("login") &&
          !error.response?.request.url.includes("user/me") &&
          !error.response?.request.url.includes("user/me/groups") &&
          error.response?.status === 401
        ) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ColorModeProvider>
        <RouterProvider router={router} />
      </ColorModeProvider>
    </StyledEngineProvider>
  );
};
export default App;
